"use client";

import { Button, buttonVariants } from "@/components/ui/button";
import {
    AlertDialog,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { LoaderCircle, Trash } from "lucide-react";
import { useState } from "react";
import { client } from "@/lib/hono";
import toast from "@/lib/toast";
import { GetPresentationResponseType } from "../../../hooks/useGetPresentation";
import { useStorage } from "@/features/storage/useStorage";
import { useQueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { t } from "@/lib/localization";

interface DeletePresentationButtonProps {
    presentation: GetPresentationResponseType["data"];
}

export default function DeletePresentationButton({
    presentation,
}: DeletePresentationButtonProps) {
    const setOpen = useStorage((state) => state.setPresentationSheetOpen);

    const [isPending, setIsPending] = useState(false);

    const queryClient = useQueryClient();
    const deleteDescription = t("storage.delete-presentation-modal.header.description", undefined, undefined);
    const [deleteDescriptionBefore, deleteDescriptionAfter] =
        deleteDescription.split("{presentationName}");

    const closeModal = () => {
        setOpen(false);
        setIsPending(false);
    };

    const submitHandler = async () => {
        setIsPending(true);

        const res = await client.api.storage.presentation[":projectId"].$delete(
            {
                param: {
                    projectId: presentation.id,
                },
            }
        );

        if (!res.ok) {
            const error = await res.text();
            console.log(error);
            toast.error(t("toast.error.delete-presentation"), {
                keyName: "toast.error.delete-presentation",
            });
        } else {
            const data = await res.json();
            console.log(data);

            queryClient.invalidateQueries({ queryKey: ["get-storage-files"] });

            toast.success(t("toast.success.delete-presentation"), {
                keyName: "toast.success.delete-presentation",
            });
            closeModal();
        }
    };

    return (
        <AlertDialog>
            <AlertDialogTrigger>
                <div
                    className={cn(
                        buttonVariants({
                            variant: "secondary",
                            size: "sm",
                        })
                    )}
                >
                    <Trash className="size-3.5 me-2" />
                    {t("presentation-sheet.header.delete-btn", undefined, undefined)}
                </div>
            </AlertDialogTrigger>
            <AlertDialogContent className="sm:max-w-[380px]">
                <AlertDialogHeader>
                    <AlertDialogTitle>
                        {t("storage.delete-presentation-modal.header.title", undefined, undefined)}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                        {deleteDescriptionBefore}
                        <span className="font-semibold">
                            {presentation?.name || ""}
                        </span>
                        {deleteDescriptionAfter || ""}
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter className="flex flex-row gap-3">
                    <AlertDialogCancel
                        disabled={isPending}
                        className="flex justify-center items-center"
                    >
                        {t("storage.delete-presentation-modal.cancel-btn", undefined, undefined)}
                    </AlertDialogCancel>
                    <Button
                        disabled={isPending}
                        onClick={() => submitHandler()}
                        variant="destructive"
                        className="flex justify-center items-center"
                    >
                        {isPending ? (
                            <LoaderCircle className="size-4 text-white animate-spin" />
                        ) : (
                            t("storage.delete-presentation-modal.delete-btn", undefined, undefined)
                        )}
                    </Button>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}
